import { datadogRum } from "@datadog/browser-rum"
import { gtmTracker } from "tracker/tracker"

/*
 * Shared classification for "the auth provider is unreachable" (INC-105 class)
 * versus a genuine auth outcome the app must keep handling as it does today.
 */

export const AUTH_PROVIDER_UNAVAILABLE_ERROR = "AUTH_PROVIDER_UNAVAILABLE"

// Auth0 answered with an auth decision, not an availability problem.
const NON_OUTAGE_ERROR_CODES = [
  "invalid_grant",
  "login_required",
  "consent_required",
  "interaction_required",
  "access_denied",
  "mfa_required",
  "unauthorized",
  "invalid_request",
  "cancelled"
]

// Provider-side failure codes; spa-js reports a 5xx token response as one of
// these, with no HTTP status.
const OUTAGE_ERROR_CODES = [
  "timeout",
  "request_error",
  "server_error",
  "temporarily_unavailable"
]

export class AuthProviderUnavailableError extends Error {
  constructor(reason) {
    super(`Auth provider unavailable: ${reason}`)
    this.name = "AuthProviderUnavailableError"
    this.error = AUTH_PROVIDER_UNAVAILABLE_ERROR
    this.reason = reason
  }
}

export const isAuthProviderUnavailable = error => {
  if (!error) return false
  if (error instanceof AuthProviderUnavailableError) return true

  const code = error.error
  if (code) {
    if (code === AUTH_PROVIDER_UNAVAILABLE_ERROR) return true
    if (NON_OUTAGE_ERROR_CODES.includes(code)) return false
    return OUTAGE_ERROR_CODES.includes(code)
  }

  // 5xx is an outage, 4xx is an answer.
  const status = error.response?.status ?? error.status
  if (typeof status === "number") return status >= 500

  // No code and no status: only a real failed network attempt, not a bare
  // programming error (which would fire a false outage signal).
  return Boolean(
    (error.request && !error.response) ||
      error.code === "ERR_NETWORK" ||
      error.code === "ECONNABORTED"
  )
}

export const reportAuthProviderUnavailable = (error, { source } = {}) => {
  try {
    datadogRum.addError(error, {
      feature: "auth_provider_unavailable",
      source
    })
  } catch (e) {
    // RUM not initialized (tests, local without keys) - telemetry is best-effort
  }
  gtmTracker.trackEvent("auth_provider_unavailable", {
    page_url: window.location.href,
    main_feature: "authentication",
    location: source
  })
}
